import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  listPeople,
  listHappenings,
  listTasks,
  listPlaces,
  listThings,
} from '@littlebooker/dal';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Search, Users, Calendar, CheckSquare, MapPin, Package, LayoutDashboard, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

type SearchResult = {
  id: string;
  type: 'person' | 'happening' | 'task' | 'place' | 'thing' | 'page';
  title: string;
  subtitle?: string;
  href: string;
  entityId?: string; // The actual entity ID for non-page results
};

const PAGES: SearchResult[] = [
  { id: 'page-dashboard', type: 'page', title: 'Dashboard', subtitle: 'Overview of bookings', href: '/' },
  { id: 'page-people', type: 'page', title: 'People', subtitle: 'Artists, staff, and guests', href: '/people' },
  { id: 'page-happenings', type: 'page', title: 'Happenings', subtitle: 'Shows, events, recordings', href: '/happenings' },
  { id: 'page-tasks', type: 'page', title: 'Tasks', subtitle: 'Work assignments', href: '/tasks' },
  { id: 'page-places', type: 'page', title: 'Places', subtitle: 'Rooms and venues', href: '/places' },
  { id: 'page-things', type: 'page', title: 'Things', subtitle: 'Equipment and items', href: '/things' },
];

const TYPE_ICONS: Record<SearchResult['type'], typeof Users> = {
  person: Users,
  happening: Calendar,
  task: CheckSquare,
  place: MapPin,
  thing: Package,
  page: LayoutDashboard,
};

// Per-entity cap on server-side results. Five types, so the list stays
// scannable while leaving room for the best matches in each.
const PER_TYPE_LIMIT = 10;
const DEBOUNCE_MS = 250;

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  // Debounce before hitting the database, so a burst of keystrokes is one
  // round trip per entity type rather than one per character.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Search runs in the database, not over a copy of the tables held in the
  // browser. The old version fetched every person, happening, task, place and
  // thing on open and matched in JS, which meant the API row cap silently
  // truncated what was searchable once a table grew past it — results simply
  // went missing with no error. Asking the server per keystroke has no such
  // ceiling and moves far less data.
  const term = debouncedQuery.trim();
  const searchEnabled = open && term.length > 0;
  const searchQueryOptions = {
    enabled: searchEnabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  };

  const { data: people = [], isFetching: peopleLoading } = useQuery({
    queryKey: ['globalSearch', 'people', term],
    queryFn: () => listPeople({ query: term, limit: PER_TYPE_LIMIT }),
    ...searchQueryOptions,
  });

  const { data: happenings = [], isFetching: happeningsLoading } = useQuery({
    queryKey: ['globalSearch', 'happenings', term],
    queryFn: () =>
      // withParticipants: false — the result rows show only a title and a
      // status, so there is no reason to drag every happening's cast along.
      listHappenings({ query: term, limit: PER_TYPE_LIMIT, withParticipants: false }),
    ...searchQueryOptions,
  });

  const { data: tasks = [], isFetching: tasksLoading } = useQuery({
    queryKey: ['globalSearch', 'tasks', term],
    queryFn: () => listTasks({ query: term, limit: PER_TYPE_LIMIT }),
    ...searchQueryOptions,
  });

  const { data: places = [], isFetching: placesLoading } = useQuery({
    queryKey: ['globalSearch', 'places', term],
    queryFn: () => listPlaces({ query: term, limit: PER_TYPE_LIMIT }),
    ...searchQueryOptions,
  });

  const { data: things = [], isFetching: thingsLoading } = useQuery({
    queryKey: ['globalSearch', 'things', term],
    queryFn: () => listThings({ query: term, limit: PER_TYPE_LIMIT }),
    ...searchQueryOptions,
  });

  const isSearching =
    peopleLoading || happeningsLoading || tasksLoading || placesLoading || thingsLoading;
  // True while the user has typed something the debounce has not yet sent.
  const isPending = query.trim() !== term;

  // Map server results into the flat result list. No filtering happens here:
  // the database already decided what matches.
  const filteredResults = useMemo<SearchResult[]>(() => {
    if (!term) {
      return PAGES; // Show pages when no query
    }

    const results: SearchResult[] = [];

    people.forEach((person) => {
      results.push({
        id: `person-${person.id}`,
        type: 'person',
        // name is nullable; fall back to the slug so the row is never blank.
        title: person.name ?? person.slug,
        subtitle: `${person.type} • ${person.slug}`,
        href: '/people',
        entityId: person.id,
      });
    });

    happenings.forEach((happening) => {
      // Every instance of a series carries the same title, so without the
      // episode number the results are a column of identical rows. Keyed on
      // the number being present rather than on show_type, so anything
      // numbered says so.
      const episode = happening.metadata?.episode_number;
      results.push({
        id: `happening-${happening.id}`,
        type: 'happening',
        title: happening.title,
        subtitle: [
          happening.type,
          typeof episode === 'number' ? `Episode ${episode}` : null,
          happening.status,
        ]
          .filter(Boolean)
          .join(' • '),
        href: '/happenings',
        entityId: happening.id,
      });
    });

    tasks.forEach((task) => {
      results.push({
        id: `task-${task.id}`,
        type: 'task',
        title: task.title,
        subtitle: `${task.type} • ${task.status}`,
        href: '/tasks',
        entityId: task.id,
      });
    });

    places.forEach((place) => {
      results.push({
        id: `place-${place.id}`,
        type: 'place',
        title: place.name,
        subtitle: place.type,
        href: '/places',
        entityId: place.id,
      });
    });

    things.forEach((thing) => {
      results.push({
        id: `thing-${thing.id}`,
        type: 'thing',
        title: thing.name,
        subtitle: `${thing.code} • ${thing.category} • ${thing.status}`,
        href: '/things',
        entityId: thing.id,
      });
    });

    // Exact code → jump: if the query is a complete code, surface that thing
    // first (auto-selected, so Enter goes straight there) regardless of any
    // other substring noise below it.
    const exactThing = things.find(
      (thing) => thing.code?.toUpperCase() === term.toUpperCase()
    );
    if (exactThing) {
      const exactId = `thing-${exactThing.id}`;
      const dupeIdx = results.findIndex((r) => r.id === exactId);
      if (dupeIdx >= 0) results.splice(dupeIdx, 1);
      results.unshift({
        id: exactId,
        type: 'thing',
        title: exactThing.name,
        subtitle: `${exactThing.code} • exact code match`,
        href: '/things',
        entityId: exactThing.id,
      });
    }

    return results;
  }, [term, people, happenings, tasks, places, things]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredResults]);

  // Handle keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle selection
  const handleSelect = useCallback(
    (result: SearchResult) => {
      // For entities, pass the ID via router state so the page can open it directly
      if (result.entityId) {
        navigate(result.href, {
          state: {
            openEntityId: result.entityId,
            entityType: result.type,
            timestamp: Date.now(),
          }
        });
      } else {
        navigate(result.href);
      }
      setOpen(false);
      setQuery('');
    },
    [navigate]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredResults.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredResults[selectedIndex]) {
          handleSelect(filteredResults[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [filteredResults, selectedIndex, handleSelect]
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent size="medium" className="gap-0 overflow-hidden p-0">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">
          Search for people, happenings, tasks, places, things, or navigate to pages
        </DialogDescription>
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search everything..."
            className="flex h-12 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
          <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {/* Distinguish "still looking" from "nothing there". With the search
              now going to the server, an in-flight query used to render as a
              flat "No results found." */}
          {filteredResults.length === 0 && (isSearching || isPending) ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Searching…
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </div>
          ) : (
            <div className="p-2">
              {filteredResults.map((result, index) => {
                const Icon = TYPE_ICONS[result.type];
                return (
                  <button
                    key={result.id}
                    type="button"
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                      selectedIndex === index
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate font-medium">{result.title}</p>
                      {result.subtitle && (
                        <p className="truncate text-xs text-muted-foreground">
                          {result.subtitle}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between border-t bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <div className="flex gap-2">
            <span>
              <kbd className="rounded border bg-background px-1">↑</kbd>
              <kbd className="rounded border bg-background px-1">↓</kbd>
              {' '}to navigate
            </span>
            <span>
              <kbd className="rounded border bg-background px-1">↵</kbd>
              {' '}to select
            </span>
          </div>
          <span>
            <kbd className="rounded border bg-background px-1">esc</kbd>
            {' '}to close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
